// Sell unit (products.unit): what one ordered quantity of a product means — "case",
// "each", "lb". Shared by the product routes (server) and the product form (client).
// Pure functions only.
//
// Free text, not an enum: tenants sell by the flat, the bushel, the pint. It is stored
// in one canonical form — trimmed, inner whitespace collapsed, lowercased — so a rule
// keyed on it (a customer type that hides "each" and "lb",
// BRIEF-customer-types-zones-picksheet.md §2a) matches however the admin typed it.

export const PRODUCT_UNIT_MAX = 30; // products.unit is varchar(30)

// The final format a customer receives — what the picker hands over. A pack inside it
// (a bunch, a pint, a 2 lb bag) belongs in the title: "Dill Bunch" [each],
// "Grape Cherry Tomatoes - 12x1pt" [case]. `each` is any single; `bag` is a bag sold as
// one (10–25 lb), `sack` a 50 lb one (Jon, 2026-09-30).
export const PRODUCT_UNIT_SUGGESTIONS = [
  "case",
  "each",
  "bag",
  "sack",
  "box",
  "bushel",
  "bin",
  "lb",
] as const;

/**
 * The stored form of a unit the admin typed: trimmed, whitespace collapsed,
 * lowercased. Blank → null (no unit). Length is the caller's check: an over-long
 * unit is refused, never cut short.
 */
export function normalizeProductUnit(raw: string): string | null {
  const unit = raw.replace(/\s+/g, " ").trim().toLowerCase();
  return unit.length > 0 ? unit : null;
}

/**
 * The unit choices the product form offers as chips: the org's units in use on active
 * products, most-used first, then the common suggestions it doesn't use yet.
 */
export function productUnitChoices(
  products: { unit?: string | null; isActive: boolean }[],
): { inUse: { unit: string; count: number }[]; suggested: string[] } {
  const counts = new Map<string, number>();
  for (const p of products) {
    const unit = p.unit ? normalizeProductUnit(p.unit) : null;
    if (unit && p.isActive) counts.set(unit, (counts.get(unit) ?? 0) + 1);
  }
  const inUse = Array.from(counts, ([unit, count]) => ({ unit, count })).sort(
    (a, b) => b.count - a.count || a.unit.localeCompare(b.unit),
  );
  const suggested = PRODUCT_UNIT_SUGGESTIONS.filter((unit) => !counts.has(unit) && !findSpellingOf(unit, inUse));
  return { inUse, suggested };
}

// Common spellings of one unit. Matching only — nothing is ever rewritten: the admin
// picks the spelling, and "lbs" stays "lbs" if they keep it.
const UNIT_SYNONYMS: Record<string, string> = {
  lbs: "lb",
  pound: "lb",
  pounds: "lb",
  "#": "lb",
  cs: "case",
  cases: "case",
  ea: "each",
  pc: "each",
  pcs: "each",
  piece: "each",
  pieces: "each",
  unit: "each",
  units: "each",
  bu: "bushel",
  bushels: "bushel",
  doz: "dozen",
  dz: "dozen",
  dozens: "dozen",
  bags: "bag",
  sacks: "sack",
  bunches: "bunch",
  bn: "bunch",
  bch: "bunch",
  flats: "flat",
  pints: "pint",
  pt: "pint",
  cartons: "carton",
  ctn: "carton",
  kgs: "kg",
  kilo: "kg",
  kilos: "kg",
  kilogram: "kg",
  kilograms: "kg",
  boxes: "box",
  bx: "box",
};

/** One spelling key per unit: "Lbs." / "pounds" / "lb" → "lb". Unknown units key as themselves. */
function unitKey(unit: string): string {
  const normalized = normalizeProductUnit(unit)?.replace(/\.$/, "") ?? "";
  return UNIT_SYNONYMS[normalized] ?? normalized;
}

function findSpellingOf(unit: string, inUse: { unit: string; count: number }[]) {
  const key = unitKey(unit);
  return inUse.find((u) => u.unit !== unit && unitKey(u.unit) === key);
}

/**
 * A unit the org already uses under another spelling of what was typed ("lbs" while
 * "lb" is in use) — the product form's hint. Null when the typed unit is new, blank,
 * or already one of the org's own spellings.
 */
export function existingSpellingOf(
  typed: string,
  inUse: { unit: string; count: number }[],
): { unit: string; count: number } | null {
  const unit = normalizeProductUnit(typed);
  if (!unit || inUse.some((u) => u.unit === unit)) return null;
  return findSpellingOf(unit, inUse) ?? null;
}
